import React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip, Button, Box } from "@mui/material";
import { CheckCircle, Error, CloudUpload, Cancel, Pending, Warning, Download } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { UploadRowState, UploadRowStatus } from "../../../../services/accountService/model/account/OutOfPeriodAdjustment";
import { useAppSelector } from "../../../../app/hooks";

interface StatusCellProps {
    dataItem: UploadRowState;
}

const StatusCell: React.FC<StatusCellProps> = ({ dataItem }) => {
    const getStatusDisplay = (status: UploadRowStatus, errorMessage?: string, warningMessage?: string) => {
        switch (status) {
            case 'pending':
                return (
                    <Tooltip title="Pending">
                        <Pending sx={{ color: '#9e9e9e' }} />
                    </Tooltip>
                );
            case 'validating':
                return (
                    <Tooltip title="Validating...">
                        <CircularProgress size={20} sx={{ color: '#ff9800' }} />
                    </Tooltip>
                );
            case 'valid':
                return (
                    <Tooltip title="Valid - Ready to upload">
                        <CheckCircle sx={{ color: '#4caf50' }} />
                    </Tooltip>
                );
            case 'valid-warning':
                return (
                    <Tooltip title={warningMessage || 'Valid with warning - Ready to upload'}>
                        <Warning sx={{ color: '#ff9800' }} />
                    </Tooltip>
                );
            case 'invalid':
                return (
                    <Tooltip title={errorMessage || 'Validation failed'}>
                        <Error sx={{ color: '#f44336' }} />
                    </Tooltip>
                );
            case 'uploading':
                return (
                    <Tooltip title="Uploading...">
                        <CircularProgress size={20} sx={{ color: '#2196f3' }} />
                    </Tooltip>
                );
            case 'uploaded':
                return (
                    <Tooltip title="Successfully uploaded">
                        <CloudUpload sx={{ color: '#4caf50' }} />
                    </Tooltip>
                );
            case 'failed':
                return (
                    <Tooltip title={errorMessage || 'Upload failed'}>
                        <Cancel sx={{ color: '#f44336' }} />
                    </Tooltip>
                );
            default:
                return null;
        }
    };

    return (
        <td style={{ textAlign: 'center' }}>
            {getStatusDisplay(dataItem.status, dataItem.errorMessage, dataItem.warningMessage)}
        </td>
    );
};

const OPAUploadGrid: React.FC = () => {
    const { rows, phase } = useAppSelector(state => state.opaUpload);

    // Status sort order: errors first, then warnings, then success, then pending
    const statusSortOrder: Record<UploadRowStatus, number> = {
        'invalid': 0,
        'failed': 1,
        'valid-warning': 2,
        'valid': 3,
        'uploaded': 4,
        'pending': 5,
        'validating': 6,
        'uploading': 7
    };

    // Status labels for export
    const statusLabels: Record<UploadRowStatus, string> = {
        'invalid': 'Invalid',
        'failed': 'Failed',
        'valid-warning': 'Warning',
        'valid': 'Valid',
        'uploaded': 'Uploaded',
        'pending': 'Pending',
        'validating': 'Validating',
        'uploading': 'Uploading'
    };

    const gridData = rows.map(row => ({
        ...row,
        accountNumber: row.data.accountNumber,
        period: row.data.period,
        year: row.data.year,
        amount: row.data.amount,
        reason: row.data.reason
    }));

    // Sort only in review and complete phases
    const shouldSort = phase === 'review' || phase === 'complete';
    const sortedData = shouldSort 
        ? [...gridData].sort((a, b) => statusSortOrder[a.status] - statusSortOrder[b.status])
        : gridData;

    const handleExportCSV = () => {
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 10) + '_' + 
            now.toTimeString().slice(0, 8).replace(/:/g, '');
        const filename = `OPA_Upload_Results_${timestamp}.csv`;

        const headers = ['LineNumber', 'AccountNumber', 'Period', 'Year', 'Amount', 'Reason', 'Status', 'Message'];
        const csvRows = [headers.join(',')];

        for (const row of sortedData) {
            const message = row.errorMessage || row.warningMessage || '';
            const escapedReason = `"${(row.reason || '').replace(/"/g, '""')}"`;
            const escapedMessage = `"${message.replace(/"/g, '""')}"`;
            
            csvRows.push([
                row.lineNumber,
                row.accountNumber,
                row.period,
                row.year,
                row.amount,
                escapedReason,
                statusLabels[row.status],
                escapedMessage
            ].join(','));
        }

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const StatusColumnCell = (props: any) => {
        return <StatusCell dataItem={props.dataItem} />;
    };

    const AmountCell = (props: any) => {
        const amount = parseFloat(props.dataItem.amount);
        const formatted = isNaN(amount) 
            ? props.dataItem.amount 
            : amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        return <td style={{ textAlign: 'right' }}>{formatted}</td>;
    };

    const getRowClassName = (dataItem: any) => {
        switch (dataItem.status) {
            case 'valid':
            case 'uploaded':
                return 'row-success';
            case 'valid-warning':
                return 'row-warning';
            case 'invalid':
            case 'failed':
                return 'row-error';
            case 'validating':
            case 'uploading':
                return 'row-processing';
            default:
                return '';
        }
    };

    return (
        <React.Fragment>
            <style>
                {`
                    .row-success { background-color: #e8f5e9 !important; }
                    .row-warning { background-color: #fff3e0 !important; }
                    .row-error { background-color: #ffebee !important; }
                    .row-processing { background-color: #e3f2fd !important; }
                `}
            </style>
            <Box display="flex" justifyContent="flex-end" mb={1}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={handleExportCSV}
                >
                    Export to CSV
                </Button>
            </Box>
            <Grid 
                data={sortedData} 
                style={{ height: '400px', maxHeight: '400px' }}
                rowRender={(row, props) => {
                    const className = getRowClassName(props.dataItem);
                    return React.cloneElement(row, { className: `${row.props.className || ''} ${className}`.trim() });
                }}
            >
                <Column 
                    field="lineNumber" 
                    title="Line #" 
                    width="80px" 
                />
                <Column 
                    field="accountNumber" 
                    title="Account" 
                    width="120px" 
                />
                <Column 
                    field="period" 
                    title="Period" 
                    width="90px" 
                />
                <Column 
                    field="year" 
                    title="Year" 
                    width="80px" 
                />
                <Column 
                    field="amount" 
                    title="Amount" 
                    width="100px" 
                    cell={AmountCell}
                />
                <Column 
                    field="reason" 
                    title="Reason" 
                />
                <Column 
                    title="Status" 
                    width="80px" 
                    cell={StatusColumnCell}
                />
            </Grid>
        </React.Fragment>
    );
};

export { OPAUploadGrid };
